<template>
  <div ref="annSidebar" class="annotation-sidebar">
    <div
      v-if="showSidebar"
      class="extended-annotations puller"
      :style="{ top: `${annotationsTop}px` }"
    >
      <AnnotationChipItem
        v-for="annotation in annsWithNote"
        :key="annotation.id"
        :annotation="annotation"
        :show-note-mode="true"
        :inside-book="true"
      ></AnnotationChipItem>
    </div>
    <div v-if="showSidebar" class="annotations puller">
      <div
        v-for="annotation in annsWithNote"
        :key="annotation.id"
        :ref="annotation.id"
        class="annotation-wrapper"
        :style="{ top: `${annotation.top}px` }"
        :class="getAnnClasses(annotation)"
        @click="onAnnIconClick(annotation.top)"
      >
        <div
          :class="getStyleByAnnCategory(annotation.categoryId)"
          class="icon annotation"
          :annotation-id="annotation.id"
          :char-offset="annotation.start.logicalCharOffset"
        >
          {{ getNotesCountLabel(annotation.top) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AnnotationChipItem from '../Extras/NotesTab/AnnotationChipItem/AnnotationChipItem';
import CustomErrors from '@/enums/CustomErrorEnum';
import MarkerUtils from '@shared/publication/dom-utils/marker-utils.mjs';
import get from 'lodash/get';
import difference from 'lodash/difference';
import VueErrorHelper from '@/services/utils/VueErrorHelper';
import AppConstantsUtil from '@/services/utils/AppConstantsUtil';
import AnnotationDomUtils from '@shared/publication/annotation/AnnotationDomUtils';
import AnnotationSidebarEvents from '@/enums/AnnotationSidebarEvents';
import PopupNamesEnum from '@/enums/PopupNamesEnum';
import Annotation from '@/services/utils/Annotation';
import LayoutManager from '@/services/utils/LayoutManager';
import PromiseUtil from '@/services/utils/PromiseUtil';

import LoggerFactory from '@/services/utils/LoggerFactory';
const logger = LoggerFactory.getLogger('AnnotationSidebar.vue');

const PARA_ATTRIBUTE = 'data-ww';
const ACTIVE_CLASS = 'active';
const ANN_ID_MARKER = MarkerUtils.getAnnotationIdMarker();
const BOOKMARK_ID_MARKER = MarkerUtils.getBookmarkIdMarker();

const EXTENDED_ANN_HEIGHT = 118;

const MouseEvent = typeof window === 'undefined' ? Object : window.MouseEvent;

export default {
  name: 'AnnotationSidebar',
  components: {
    AnnotationChipItem
  },
  props: {
    showSidebar: {
      type: Boolean,
      required: true
    },
    bookId: {
      type: String,
      required: true
    },
    containerSelector: String,
    clickEventHandler: {
      type: [Function, MouseEvent]
    }
  },
  data() {
    return {
      paraId: '',
      blockId: '',
      extendedAnnHeight: EXTENDED_ANN_HEIGHT,
      ANN_ID_MARKER,
      BOOKMARK_ID_MARKER,
      annotations: [],
      bookmarks: [],
      isNotesInOneLine: true,
      endSubscription: null,
      annotationsTop: 0
    };
  },
  computed: {
    annsWithNote() {
      return this.annotations.filter(annotation => annotation.note);
    },
    isExtendedNotesModeOn() {
      return this.$store.getters['ReadingSettingsStore/getExpandedNotesMode'];
    },
    notesCountByTop() {
      const notesCountByTop = this.annsWithNote.reduce(
        (notesTop, annotation) => {
          const top = annotation.top;
          if (notesTop[top]) {
            notesTop[top]++;
          } else {
            notesTop[top] = 1;
          }
          return notesTop;
        },
        {}
      );
      return notesCountByTop;
    },
    isAudioPlaying() {
      return this.$store.state.PlaybackStore.isPlaying;
    },
    getFontName() {
      return this.$store.getters['ReadingSettingsStore/getFontName'];
    },
    getFontSize() {
      return this.$store.getters['ReadingSettingsStore/getFontSize'];
    },
    getLineSpacing() {
      return this.$store.getters['ReadingSettingsStore/getLineSpacing'];
    },
    isExtrasOpened() {
      return this.$store.getters['ManagePopupStore/isPopupOpened'](
        PopupNamesEnum.EXTRAS_POPUP
      );
    },
    isExtrasComponentOpen() {
      const mediaSize = this.$store.getters['MediaDetectorStore/mediaSize'];
      return this.isExtrasOpened && mediaSize.wide;
    }
  },
  watch: {
    clickEventHandler: {
      handler(clickInfo) {
        if (!this.showSidebar) {
          return;
        }
        try {
          this.$_clickEventHandler(clickInfo);
        } catch (error) {
          this.$_annSidebarErrorHandler(error);
        }
      }
    },
    isExtendedNotesModeOn(isOn) {
      if (!isOn) {
        this.isNotesInOneLine = true;
      }
      this.$_reinitAnnotationsTop();
      this.$_moveSidebarAnnotations();
    },
    showSidebar() {
      this.calcSidebarParams();
      this.$_moveSidebarAnnotations();
    },
    paraId() {
      this.initMarkMaterials();
      this.$_moveSidebarAnnotations();
    },
    containerSelector() {
      if (!this.paraId) {
        this.calcSidebarParams();
      }
    },
    isAudioPlaying(isPlaying) {
      if (!isPlaying) {
        this.$store.dispatch('AnnotationsStore/setActiveAnnIds', {});
        this.$_resetAnnsParameters();
      }
    },
    getFontSize() {
      this.reinitAnnsTopHandler();
    },
    getLineSpacing() {
      this.reinitAnnsTopHandler();
    },
    getFontName() {
      this.reinitAnnsTopHandler();
    },
    isExtrasComponentOpen() {
      this.reinitAnnsTopHandler();
    }
  },
  mounted() {
    this.calcSidebarParams();
    this.$_subscribe();
    if (this.paraId) {
      LayoutManager.register({
        layout: this.reinitAnnsTopHandler,
        id: this.$_getLayoutId()
      });
    }
  },
  destroyed() {
    if (this.endSubscription) {
      this.endSubscription();
    }
    if (this.paraId) {
      LayoutManager.unregister(this.$_getLayoutId());
    }
  },
  errorCaptured(error, vm) {
    const trace = VueErrorHelper.generateComponentTrace(vm);
    this.$_annSidebarErrorHandler(`${error}\n${trace}`);
  },
  methods: {
    calcSidebarParams() {
      const paragraphElement = this.$_getParagraphElement();
      if (!paragraphElement) {
        return;
      }
      this.paraId = paragraphElement.getAttribute('id') || '';
      this.blockId = paragraphElement.dataset?.ilmid || '';
    },
    $_getScrollContainer() {
      return window.document.querySelector(this.containerSelector);
    },
    $_getParagraphElement() {
      return this.$el.parentElement?.querySelector(`[${PARA_ATTRIBUTE}]`);
    },
    initMarkMaterials() {
      this.initBookmarks();
      this.initAnnotations();
    },
    initAnnotations() {
      const container =
        this.$_getParagraphElement() || this.$_getScrollContainer();
      this.annotations = this.getAnnotationViewsByBlockId();

      const length = this.annotations.length;
      let annotation;
      for (let index = length - 1; index >= 0; index--) {
        try {
          annotation = this.annotations[index];
          this.highlightAnnotation(annotation);
          annotation.initAnnotation(container);
        } catch (err) {
          if (err === CustomErrors.ANNOTATION_RANGE_ERROR) {
            logger.warn(
              `Incorrect ranges for annotation with id ${annotation.id}`
            );
          } else {
            logger.error(
              `Error while highlighting ann with id ${annotation.id}: ${err}`
            );
          }
          this.annotations.splice(index, 1);
        }
      }
    },
    initBookmarks() {
      this.bookmarks = this.getBookmarksByBlockId();
      this.bookmarks.forEach(bookmark => {
        try {
          this.highlightBookmark(bookmark);
        } catch (err) {
          logger.error(
            `Error while highlighting bookmark with id ${bookmark.id}: ${err}`
          );
        }
      });
    },
    async reinitAnnsTopHandler() {
      this.$_reinitAnnotationsTop();
      await this.$_moveSidebarAnnotations();
    },
    $_getLayoutId() {
      return `sidebar_${this.paraId}`;
    },
    $_subscribe() {
      this.endSubscription = this.$store.subscribe(({ payload, type }) => {
        const annotation = payload?.annotation;
        const paraId =
          payload?.targetParaId ||
          annotation?.paragraphId ||
          payload?.paragraphId;
        switch (type) {
          case 'MaterialsStore/changeMaterialsLoaded':
            if (payload) {
              this.initMarkMaterials();
            }
            break;
          case 'AnnotationsStore/clearStore':
            this.$_deleteAllMarkMaterials();
            break;
          case 'BookmarkStore/addBookmark':
            this.addBookmarkByParaId(payload?.bookmark);
            break;
          case 'BookmarkStore/deleteBookmarkProcess':
            if (payload.bookmark) {
              this.$_deleteBookmark(payload.bookmark);
            }
            break;
        }
        if (
          get(annotation, 'start.prefixedParagraphId', null) !== this.paraId &&
          paraId !== this.paraId
        ) {
          return;
        }

        try {
          switch (type) {
            case 'AnnotationsStore/addAnnotation':
              this.$_addNewAnnotation(annotation);
              break;
            case 'AnnotationsStore/changeAnnotation':
              this.$_changeAnnotation(annotation);
              break;
            case 'AnnotationsStore/deleteAnnotationProcess':
              this.$_deleteAnnotation(annotation);
              break;
            case 'AnnotationsStore/deleteAllAnnotationProcess':
              this.$_deleteAllAnnotation();
              break;
            case 'AnnotationsStore/deleteAnnotationsByCategory':
              if (payload?.categoryId) {
                this.$_deleteAnnByCategory(payload.categoryId);
              }
              break;
            case 'AnnotationsStore/selectActiveAnnotation':
              if (this.isExtendedNotesModeOn) {
                this.$_removeAllSelected();
                this.$_selectActiveAnnotations(payload.annIds);
              }
              break;
          }
        } catch (err) {
          this.$_annSidebarErrorHandler(err);
        }
      });
    },
    getNotesCountLabel(top) {
      const notesCount = this.notesCountByTop[top];
      return notesCount > 1 ? notesCount : '';
    },
    onAnnIconClick(annTop) {
      const annIdsByTop = this.$_getAnnIdsByTop(annTop);
      this.openAnnotationInfoPopup(annIdsByTop);
    },
    $_getAnnIdsByTop(annTop) {
      const annIdsByTop = this.annsWithNote.reduce((annsIds, annotation) => {
        if (annotation.top === annTop) {
          annsIds.push(annotation.id);
        }
        return annsIds;
      }, []);
      return annIdsByTop;
    },
    $_annSidebarErrorHandler(error) {
      logger.fatal(`Error in Annotation sidebar: ${error.stack || error}`);
      this.emit(AnnotationSidebarEvents.ERROR, { error });
    },
    emit(type, data) {
      this.$emit('annotationSidebarEvent', {
        type,
        data
      });
    },
    $_reinitAnnotationsTop() {
      const container =
        this.$_getParagraphElement() || this.$_getScrollContainer();
      this.annsWithNote.forEach(annotation =>
        annotation.reinitTopOffset(container)
      );
    },
    $_clickEventHandler(clickInfo) {
      const {
        paraElement,
        itemElement,
        annotationIds,
        bookmarkId,
        openMaterial
      } = _extractParaItemsFromBubblingPath(clickInfo);

      if (!itemElement) {
        return;
      }

      this.isNotesInOneLine = true;

      try {
        this.$_removeSelectedAnnotation();
        if (paraElement?.getAttribute('id') !== this.paraId) {
          return;
        }

        if (openMaterial === BOOKMARK_ID_MARKER && bookmarkId) {
          this.$store.dispatch('BookmarkStore/setBookmarkId', bookmarkId);
          this.$store.dispatch('ManagePopupStore/openPopup', {
            name: PopupNamesEnum.BOOKMARK_POPUP,
            popupContext: { activeId: bookmarkId }
          });
          return;
        }

        if (annotationIds.length) {
          this.openAnnotationInfoPopup(annotationIds);
        }
      } catch (err) {
        this.$_annSidebarErrorHandler(err);
      }

      this.onAnnotationParaClick(itemElement, paraElement, annotationIds);
    },
    openAnnotationInfoPopup(annotationIds) {
      const annotations = this.annotations.filter(annotation =>
        annotationIds.some(id => id === annotation.id)
      );
      if (!annotations.length) {
        return;
      }
      this.emit(AnnotationSidebarEvents.OPEN_INFO_POPUP, {
        blockId: annotations[0].blockId,
        paraId: annotations[0].paragraphId,
        annotationIds
      });
    },
    getStyleByAnnCategory(categoryId) {
      return this.$store.getters['CategoriesStore/getAnnClassByCategoryId'](
        categoryId
      );
    },
    getAnnotationViewsByBlockId() {
      return this.$store.getters[
        'AnnotationsStore/getAnnotationViewsByBlockId'
      ]({ bookId: this.bookId, blockId: this.blockId, paraId: this.paraId });
    },
    getBookmarksByBlockId() {
      return this.$store.getters['BookmarkStore/getBookmarksByBlockId']({
        bookId: this.bookId,
        blockId: this.blockId,
        paraId: this.paraId
      });
    },
    getAnnotationView(annotation) {
      return this.$store.getters['AnnotationsStore/getAnnotationViewById']({
        bookId: this.bookId,
        blockId: annotation.blockId,
        annId: annotation.id
      });
    },
    onAnnotationParaClick(itemElement, paraElement, annotationIds) {
      if (!this.isExtendedNotesModeOn) {
        return;
      }
      itemElement?.classList.add(ACTIVE_CLASS);
      if (!paraElement || !annotationIds.length) {
        return;
      }
      this.$_selectActiveAnnotations(annotationIds);
    },
    setAnnotionsTopInActivePara(selectedAnnIndex, selectedAnnsIds) {
      const paraBlock = this.$_getItemElement();
      paraBlock.classList.add(ACTIVE_CLASS);

      let {
        chipsHeightBeforeSelected,
        chipsMarginBeforeSelected
      } = this.$_getHeightSumBeforeSelected(selectedAnnIndex);

      if (chipsHeightBeforeSelected === 0) {
        return;
      }

      const firstAnn = document.querySelector(`.ann-${selectedAnnsIds[0]}`);
      const firstAnnRect = firstAnn.getBoundingClientRect();
      const paraBlockRect = paraBlock.getBoundingClientRect();

      this.annotationsTop =
        firstAnnRect.top -
        paraBlockRect.top -
        (chipsHeightBeforeSelected + chipsMarginBeforeSelected);
    },
    async $_selectActiveAnnotations(annIds) {
      this.isNotesInOneLine = false;

      const { selectedAnnsIds, selectedAnnIndex } = this.annsWithNote.reduce(
        (selectedAnnObj, annotation, index) => {
          annotation.isHidden = false;
          annotation.isStackable = false;
          annotation.top = 0;

          if (annIds.includes(annotation.id)) {
            annotation.isSelected = true;
            selectedAnnObj.selectedAnnsIds.push(annotation.id);
          }

          if (
            selectedAnnObj.selectedAnnIndex === -1 &&
            annotation.id === annIds[0]
          ) {
            selectedAnnObj.selectedAnnIndex = index;
          }

          return selectedAnnObj;
        },
        { selectedAnnsIds: [], selectedAnnIndex: -1 }
      );

      if (!selectedAnnsIds.length || selectedAnnIndex === -1) {
        return false;
      }

      await PromiseUtil.wait(200);
      this.addActiveMarginNotes(selectedAnnsIds);
      this.setAnnotionsTopInActivePara(selectedAnnIndex, selectedAnnsIds);
    },
    addActiveMarginNotes(selectedAnns) {
      this.$store.dispatch('AnnotationsStore/setActiveAnnIds', {
        annIds: selectedAnns,
        openFromExtras: false
      });
    },
    $_removeSelectedAnnotation() {
      if (!this.isExtendedNotesModeOn) {
        return;
      }
      const itemElement = this.$_getItemElement();
      itemElement.classList.remove(ACTIVE_CLASS);
      this.$_resetAnnsParameters();
    },
    $_resetAnnsParameters() {
      this.annsWithNote.forEach(annotation => {
        annotation.isSelected = false;
        annotation.top = annotation.initialTop;
      });
      this.$_moveSidebarAnnotations();
    },
    $_getItemElement() {
      return this.$refs.annSidebar?.closest(AppConstantsUtil.ITEM_SELECTOR);
    },
    $_getHeightSumBeforeSelected(selectedAnnIndex) {
      let annotationsBeforeSelected = this.annsWithNote.slice(
        0,
        selectedAnnIndex
      );
      return annotationsBeforeSelected.reduce(
        (acc, curValue) => {
          const el = document.querySelector(`.chip-${curValue.id}`);
          acc.chipsHeightBeforeSelected += el?.scrollHeight || 0;
          acc.chipsMarginBeforeSelected +=
            AppConstantsUtil.MARGIN_NOTES_CHIP_MARGIN;
          return acc;
        },
        { chipsHeightBeforeSelected: 0, chipsMarginBeforeSelected: 0 }
      );
    },
    highlightBookmark(bookmark) {
      const container = this.$_getScrollContainer();
      if (!container) {
        return;
      }
      const categoryClass = bookmark.categoryClass;
      AnnotationDomUtils.highlightMaterialItem(
        bookmark,
        container,
        categoryClass
      );
    },
    highlightAnnotation(annotation) {
      const scrollContainer = this.$_getScrollContainer();
      if (!scrollContainer) {
        return;
      }
      const categoryClass = this.getStyleByAnnCategory(annotation.categoryId);
      annotation.reinitLocatorPoints(this.paraId);
      AnnotationDomUtils.highlightMaterialItem(
        annotation,
        scrollContainer,
        categoryClass
      );
    },
    rehighlightAnnotation({ newAnn, oldAnn }) {
      const oldAnnClass = this.getStyleByAnnCategory(oldAnn.categoryId);
      const newAnnClass = this.getStyleByAnnCategory(newAnn.categoryId);
      const paragraphElement = this.$_getParagraphElement();

      const annHighlights = paragraphElement.querySelectorAll(
        `[${this.ANN_ID_MARKER}="${newAnn.id}"]`
      );
      annHighlights.forEach(highlight => {
        highlight.classList.remove(oldAnnClass);
        highlight.classList.add(newAnnClass);
      });
    },
    $_setExtendedAnnotationTop() {
      for (let i = 1; i < this.annsWithNote.length; i++) {
        let prevAnnTop = this.annsWithNote[i - 1].top;
        if (
          prevAnnTop &&
          this.annsWithNote[i].top - prevAnnTop < this.extendedAnnHeight
        ) {
          this.annsWithNote[i].top = prevAnnTop + this.extendedAnnHeight;
        }
      }
    },
    $_removeAllSelected() {
      this.annsWithNote.forEach(annotation => (annotation.isSelected = false));
    },
    $_addNewAnnotation(annotation) {
      const annotationView = this.getAnnotationView(annotation);
      const index = Annotation.indexToAddAnnotation(
        annotation,
        this.annotations
      );
      this.highlightAnnotation(annotationView);
      const container =
        this.$_getParagraphElement() || this.$_getScrollContainer();
      annotationView.initAnnotation(container);
      this.annotations.splice(index, 0, annotationView);

      this.$_moveSidebarAnnotations();
    },
    addBookmarkByParaId(bookmark) {
      if (bookmark?.paragraphId !== this.paraId && bookmark) {
        return;
      }
      this.bookmarks.push(bookmark);
      this.highlightBookmark(bookmark);
    },
    $_moveSidebarAnnotations() {
      this.annotationsTop = this.annsWithNote.length
        ? this.annsWithNote[0].top
        : 0;

      if (this.isExtendedNotesModeOn) {
        this.$_setExtendedAnnotationTop();
      }
    },
    $_changeAnnotation(changedAnn) {
      const index = this.annotations.findIndex(
        annotation => annotation.id === changedAnn.id
      );
      if (index === -1) {
        return;
      }
      const annotationView = this.getAnnotationView(changedAnn);
      this.rehighlightAnnotation({
        newAnn: annotationView,
        oldAnn: this.annotations[index]
      });
      const container =
        this.$_getParagraphElement() || this.$_getScrollContainer();
      annotationView.initAnnotation(container);

      let annsWithNoteLength = this.annsWithNote.length;
      this.annotations.splice(index, 1, annotationView);
      const isAnnsWithNoteLengthChanged =
        annsWithNoteLength !== this.annsWithNote.length;

      if (this.isExtendedNotesModeOn && isAnnsWithNoteLengthChanged) {
        this.$_resetAnnsParameters();
      }
    },
    $_deleteAnnByCategory(categoryId) {
      let filteredAnnotations = this.annotations.filter(
        annotation => annotation.categoryId !== categoryId
      );
      let deletedAnns = difference(this.annotations, filteredAnnotations);
      if (!deletedAnns?.length) {
        return;
      }
      this.annotations = filteredAnnotations;
      const scrollContainer = this.$_getScrollContainer();
      AnnotationDomUtils.unhighlightByAnnClass(
        this.getStyleByAnnCategory(categoryId),
        scrollContainer
      );
      this.$_resetAnnsParameters();
    },
    $_deleteAnnotation(deletedAnn) {
      this.annotations = this.annotations.filter(
        annotation => annotation.id !== deletedAnn.id
      );
      const scrollContainer = this.$_getScrollContainer();
      AnnotationDomUtils.unhighlightMaterialItem(deletedAnn, scrollContainer);
      this.$_resetAnnsParameters();
    },
    $_deleteBookmark(deletedBookmark) {
      const scrollContainer = this.$_getScrollContainer();
      this.bookmarks = this.bookmarks.filter(
        bookmark => bookmark.id !== deletedBookmark.id
      );
      AnnotationDomUtils.unhighlightMaterialItem(
        deletedBookmark,
        scrollContainer
      );
    },
    $_deleteAllMarkMaterials() {
      this.$_deleteAllAnnotation();
      this.$_deleteAllBookmarks();
    },
    $_deleteAllAnnotation() {
      const scrollContainer = this.$_getScrollContainer();
      this.annotations.forEach(deletedAnn => {
        AnnotationDomUtils.unhighlightMaterialItem(deletedAnn, scrollContainer);
      });
      this.annotations = [];
      this.$_resetAnnsParameters();
    },
    $_deleteAllBookmarks() {
      const scrollContainer = this.$_getScrollContainer();
      this.bookmarks.forEach(bookmark => {
        AnnotationDomUtils.unhighlightMaterialItem(bookmark, scrollContainer);
      });
      this.bookmarks = [];
    },
    getAnnClasses(annotation) {
      const styleByCategory = this.getStyleByAnnCategory(annotation.categoryId);
      return {
        [styleByCategory]: true,
        hidden: annotation.isHidden,
        'innactive-annotation': this.isNotesInOneLine,
        stackable: annotation.isStackable
      };
    }
  }
};

/**
 * @param {Array} clickInfo
 * @returns {Object}  paraElement, itemElement & annotationIds
 */
function _extractParaItemsFromBubblingPath(clickInfo) {
  let bubblingPath =
    clickInfo.path || (clickInfo.composedPath && clickInfo.composedPath());
  bubblingPath = bubblingPath?.slice(0, -4);

  let paraElement = null;
  let itemElement = null;
  const annotationIds = [];
  let bookmarkId = '';
  let openMaterial = '';
  bubblingPath.forEach(el => {
    if (el.hasAttribute('data-ww')) {
      paraElement = el;
    } else if (el.hasAttribute(ANN_ID_MARKER)) {
      annotationIds.push(el?.dataset?.annotationId);
      openMaterial = openMaterial || ANN_ID_MARKER;
    } else if (el.hasAttribute(BOOKMARK_ID_MARKER)) {
      bookmarkId = el?.dataset?.bookmarkId;
      openMaterial = openMaterial || BOOKMARK_ID_MARKER;
    } else if (el.classList.contains('item')) {
      itemElement = el;
    }
  });

  if (!paraElement) {
    paraElement = itemElement?.querySelector('[data-ww]');
  }

  return { paraElement, itemElement, annotationIds, bookmarkId, openMaterial };
}
</script>

<style lang="less" src="./AnnotationSidebar.less" />
